import { ProductType } from 'features/searchResultsFacets';
import { mapBuildablePlanMarker, mapCommunityMarker, mapPlanUnitMarker, mapSpecUnitMarker } from './mapper';

/**
 * Partitions plans into buildable and non-buildable groups based on community data.
 *
 * A "buildable plan" is defined as a housing development that:
 * - Has more than one total plan available (`TotalPlans > 1`), and
 * - Has more than one matching plan in the current filtered results.
 *
 * This function ensures that:
 * - Communities with multiple plans are represented as a single buildable marker
 * - Individual plans belonging to those communities are excluded from the remaining plans list
 *
 * @param {Array<Object>} housingDevelopments - List of community/housing development objects
 * @param {Array<Object>} plans - List of plan objects from the current search results
 *
 * @returns {Object} An object containing:
 * @returns {Array<Object>} return.buildablePlans - Communities that qualify as buildable plans
 * @returns {Array<Object>} return.remainingPlans - Plans not associated with buildable communities
 *
 * @example
 * const { buildablePlans, remainingPlans } =
 *   partitionPlansByBuildability(communities, plans);
 */
const partitionPlansByBuildability = (housingDevelopments, plans) => {
  const planCountMap = new Map();

  plans.forEach((plan) => {
    planCountMap.set(
      plan.CommunityId,
      (planCountMap.get(plan.CommunityId) || 0) + 1
    );
  });

  const buildablePlans = housingDevelopments.filter((dev) => {
    if (dev.TotalPlans <= 1) {
      return false;
    }

    const count = planCountMap.get(dev.Id) || 0;
    return count > 1;
  });

  const buildableCommunityIds = new Set(
    buildablePlans.map((dev) => dev.Id)
  );

  const remainingPlans = plans.filter(
    (plan) => !buildableCommunityIds.has(plan.CommunityId)
  );

  return {
    buildablePlans,
    remainingPlans,
  };
};

/**
 * Maps community data into map marker objects.
 *
 * Each community is transformed into a standardized marker
 * representation used by the map rendering layer.
 *
 * @param {Array<Object>} communities - List of community domain objects.
 * @returns {Array<Object>} Array of community markers ready for rendering.
 */
const getCommunityMarkers = (communities) =>
  communities.map(mapCommunityMarker);

/**
 * Maps home-related data into map marker objects.
 *
 * Combines specs, plans, and buildable plans into a single
 * unified list of markers for rendering on the map.
 *
 * @param {Object} params
 * @param {Array<Object>} params.specs - Spec unit data.
 * @param {Array<Object>} params.plans - Plan unit data.
 * @param {Array<Object>} params.buildablePlans - Buildable plan data.
 * @returns {Array<Object>} Array of home-related markers ready for rendering.
 */
const getHomesMarkers = ({ specs, plans, buildablePlans }) => [
  ...specs.map(mapSpecUnitMarker),
  ...plans.map(mapPlanUnitMarker),
  ...buildablePlans.map(mapBuildablePlanMarker),
];

/**
 * Transforms API data into map markers based on product type.
 *
 * Always includes community markers. When productType is 'Homes', also includes
 * spec homes and partitions plans into buildable (attached to communities) vs. standalone.
 *
 * @param {Object} payload - Contains product.productType
 * @param {Object} data - API response with communities and optional homes data
 * @returns {Array<Object>} Array of marker objects
 */
const processMarkersPayload = (payload, data) => {
  const distinctCommunities = data.communities?.CommunityResults || [];
  const productType = payload?.product?.productType;

  const allHousing = {
    communities: distinctCommunities,
    buildablePlans: [],
    specs: [],
    plans: [],
  };

  if (productType === ProductType.Homes.toString()) {
    const plans = data.homes?.Plans || [];
    const specs = data.homes?.Specs || [];
    const { buildablePlans, remainingPlans } = partitionPlansByBuildability(distinctCommunities, plans);

    allHousing.buildablePlans = buildablePlans;
    allHousing.plans = remainingPlans;
    allHousing.specs = specs;
    
    return getHomesMarkers(allHousing);
  }

  return [
    ...getCommunityMarkers(allHousing.communities),
    ...getHomesMarkers(allHousing),
  ];
};

export default processMarkersPayload;